import { supabase } from '../lib/supabase';

// ── Products Catalog (Master) ───────────────────────────────

export const productsCatalogService = {
  async getAll(statusFilter = null) {
    let query = supabase?.from('products_catalog')?.select('*')?.order('product_name', { ascending: true });

    if (statusFilter) {
      query = query?.eq('catalog_status', statusFilter);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(dbToProduct);
  },

  async getActive() {
    const { data, error } = await supabase?.from('products_catalog')?.select('id, product_name, category, oem_brand, model_sku, licensing_model, price_range, margin_category')?.eq('catalog_status', 'active')?.order('product_name', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(dbToProduct);
  },

  async create(productData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('products_catalog')?.insert({
        product_name: productData?.productName,
        category: productData?.category,
        oem_brand: productData?.oemBrand,
        model_sku: productData?.modelSku,
        licensing_model: productData?.licensingModel,
        description: productData?.description,
        target_industry: productData?.targetIndustry,
        price_range: productData?.priceRange,
        margin_category: productData?.marginCategory || 'medium',
        catalog_status: productData?.catalogStatus || 'active',
        created_by: user?.id || null,
      })?.select()?.single();
    if (error) throw error;
    return dbToProduct(data);
  },

  async update(id, productData) {
    const { data, error } = await supabase?.from('products_catalog')?.update({
        product_name: productData?.productName,
        category: productData?.category,
        oem_brand: productData?.oemBrand,
        model_sku: productData?.modelSku,
        licensing_model: productData?.licensingModel,
        description: productData?.description,
        target_industry: productData?.targetIndustry,
        price_range: productData?.priceRange,
        margin_category: productData?.marginCategory,
        catalog_status: productData?.catalogStatus,
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToProduct(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('products_catalog')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Services Catalog (Master) ───────────────────────────────

export const servicesCatalogService = {
  async getAll(statusFilter = null) {
    let query = supabase?.from('services_catalog')?.select('*')?.order('service_name', { ascending: true });

    if (statusFilter) {
      query = query?.eq('catalog_status', statusFilter);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(dbToService);
  },

  async getActive() {
    const { data, error } = await supabase?.from('services_catalog')?.select('id, service_name, service_category, delivery_model, sla_levels')?.eq('catalog_status', 'active')?.order('service_name', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(dbToService);
  },

  async create(serviceData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('services_catalog')?.insert({
        service_name: serviceData?.serviceName,
        service_category: serviceData?.serviceCategory,
        delivery_model: serviceData?.deliveryModel,
        sla_levels: serviceData?.slaLevels,
        certifications_required: serviceData?.certificationsRequired,
        description: serviceData?.description,
        industry_alignment: serviceData?.industryAlignment,
        catalog_status: serviceData?.catalogStatus || 'active',
        created_by: user?.id || null,
      })?.select()?.single();
    if (error) throw error;
    return dbToService(data);
  },

  async update(id, serviceData) {
    const { data, error } = await supabase?.from('services_catalog')?.update({
        service_name: serviceData?.serviceName,
        service_category: serviceData?.serviceCategory,
        delivery_model: serviceData?.deliveryModel,
        sla_levels: serviceData?.slaLevels,
        certifications_required: serviceData?.certificationsRequired,
        description: serviceData?.description,
        industry_alignment: serviceData?.industryAlignment,
        catalog_status: serviceData?.catalogStatus,
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToService(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('services_catalog')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Partner Products (Linking) ──────────────────────────────

export const partnerProductsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('partner_products')?.select(`
        *,
        products_catalog (
          id, product_name, category, oem_brand, model_sku,
          licensing_model, price_range, margin_category, catalog_status
        )
      `)?.eq('partner_id', partnerId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(dbToPartnerProduct);
  },

  async link(partnerId, catalogProductId, extraData = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('partner_products')?.insert({
        partner_id: partnerId,
        catalog_product_id: catalogProductId,
        margin_pct: extraData?.marginPct || 0,
        authorization_level: extraData?.authorizationLevel || 'standard',
        is_strategic: extraData?.isStrategic || false,
        link_status: 'active',
        notes: extraData?.notes || null,
        created_by: user?.id || null,
      })?.select(`*, products_catalog(id, product_name, category, oem_brand, model_sku, licensing_model, price_range, margin_category, catalog_status)`)?.single();
    if (error) throw error;
    return dbToPartnerProduct(data);
  },

  async update(id, extraData) {
    const { data, error } = await supabase?.from('partner_products')?.update({
        margin_pct: extraData?.marginPct,
        authorization_level: extraData?.authorizationLevel,
        is_strategic: extraData?.isStrategic,
        link_status: extraData?.linkStatus,
        notes: extraData?.notes,
      })?.eq('id', id)?.select(`*, products_catalog(id, product_name, category, oem_brand, model_sku, licensing_model, price_range, margin_category, catalog_status)`)?.single();
    if (error) throw error;
    return dbToPartnerProduct(data);
  },

  async unlink(id) {
    const { error } = await supabase?.from('partner_products')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Partner Services (Linking) ──────────────────────────────

export const partnerServicesService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('partner_services')?.select(`
        *,
        services_catalog (
          id, service_name, service_category, delivery_model,
          sla_levels, certifications_required, industry_alignment, catalog_status
        )
      `)?.eq('partner_id', partnerId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(dbToPartnerService);
  },

  async link(partnerId, catalogServiceId, extraData = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('partner_services')?.insert({
        partner_id: partnerId,
        catalog_service_id: catalogServiceId,
        coverage_area: extraData?.coverageArea || null,
        delivery_capability: extraData?.deliveryCapability || null,
        sla_commitment: extraData?.slaCommitment || null,
        link_status: 'active',
        notes: extraData?.notes || null,
        created_by: user?.id || null,
      })?.select(`*, services_catalog(id, service_name, service_category, delivery_model, sla_levels, certifications_required, industry_alignment, catalog_status)`)?.single();
    if (error) throw error;
    return dbToPartnerService(data);
  },

  async update(id, extraData) {
    const { data, error } = await supabase?.from('partner_services')?.update({
        coverage_area: extraData?.coverageArea,
        delivery_capability: extraData?.deliveryCapability,
        sla_commitment: extraData?.slaCommitment,
        link_status: extraData?.linkStatus,
        notes: extraData?.notes,
      })?.eq('id', id)?.select(`*, services_catalog(id, service_name, service_category, delivery_model, sla_levels, certifications_required, industry_alignment, catalog_status)`)?.single();
    if (error) throw error;
    return dbToPartnerService(data);
  },

  async unlink(id) {
    const { error } = await supabase?.from('partner_services')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Converters ──────────────────────────────────────────────

function dbToProduct(row) {
  if (!row) return null;
  return {
    id: row?.id,
    productName: row?.product_name,
    category: row?.category,
    oemBrand: row?.oem_brand,
    modelSku: row?.model_sku,
    licensingModel: row?.licensing_model,
    description: row?.description,
    targetIndustry: row?.target_industry,
    priceRange: row?.price_range,
    marginCategory: row?.margin_category,
    catalogStatus: row?.catalog_status,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
  };
}

function dbToService(row) {
  if (!row) return null;
  return {
    id: row?.id,
    serviceName: row?.service_name,
    serviceCategory: row?.service_category,
    deliveryModel: row?.delivery_model,
    slaLevels: row?.sla_levels,
    certificationsRequired: row?.certifications_required,
    description: row?.description,
    industryAlignment: row?.industry_alignment,
    catalogStatus: row?.catalog_status,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
  };
}

function dbToPartnerProduct(row) {
  if (!row) return null;
  const catalog = row?.products_catalog;
  return {
    id: row?.id,
    partnerId: row?.partner_id,
    catalogProductId: row?.catalog_product_id,
    marginPct: row?.margin_pct,
    authorizationLevel: row?.authorization_level,
    isStrategic: row?.is_strategic,
    linkStatus: row?.link_status,
    notes: row?.notes,
    createdAt: row?.created_at,
    // Flattened catalog fields for display
    productName: catalog?.product_name,
    category: catalog?.category,
    oemBrand: catalog?.oem_brand,
    modelSku: catalog?.model_sku,
    licensingModel: catalog?.licensing_model,
    priceRange: catalog?.price_range,
    marginCategory: catalog?.margin_category,
    catalogStatus: catalog?.catalog_status,
  };
}

function dbToPartnerService(row) {
  if (!row) return null;
  const catalog = row?.services_catalog;
  return {
    id: row?.id,
    partnerId: row?.partner_id,
    catalogServiceId: row?.catalog_service_id,
    coverageArea: row?.coverage_area,
    deliveryCapability: row?.delivery_capability,
    slaCommitment: row?.sla_commitment,
    linkStatus: row?.link_status,
    notes: row?.notes,
    createdAt: row?.created_at,
    // Flattened catalog fields for display
    serviceName: catalog?.service_name,
    serviceCategory: catalog?.service_category,
    deliveryModel: catalog?.delivery_model,
    slaLevels: catalog?.sla_levels,
    certificationsRequired: catalog?.certifications_required,
    industryAlignment: catalog?.industry_alignment,
    catalogStatus: catalog?.catalog_status,
  };
}

// ── Reference Tables ────────────────────────────────────────

export const slaLevelsService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('sla_levels')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('sort_order', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(r => ({ value: r?.id, label: r?.label, description: r?.description }));
  },

  async create(label, description = '') {
    const { data, error } = await supabase
      ?.from('sla_levels')
      ?.insert({ label, description })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, label, description = '') {
    const { data, error } = await supabase
      ?.from('sla_levels')
      ?.update({ label, description })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase?.from('sla_levels')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

export const certificationsService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('certifications')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('sort_order', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(r => ({ value: r?.id, label: r?.label, fullName: r?.full_name, category: r?.category }));
  },

  async create(label, fullName = '', category = '') {
    const { data, error } = await supabase
      ?.from('certifications')
      ?.insert({ label, full_name: fullName, category })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, label, fullName = '', category = '') {
    const { data, error } = await supabase
      ?.from('certifications')
      ?.update({ label, full_name: fullName, category })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase?.from('certifications')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

export const industryAlignmentsService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('industry_alignments')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('sort_order', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(r => ({ value: r?.id, label: r?.label, description: r?.description }));
  },

  async create(label, description = '') {
    const { data, error } = await supabase
      ?.from('industry_alignments')
      ?.insert({ label, description })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, label, description = '') {
    const { data, error } = await supabase
      ?.from('industry_alignments')
      ?.update({ label, description })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase?.from('industry_alignments')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Bundles Catalog (Master) ────────────────────────────────

export const bundlesCatalogService = {
  async getAll(statusFilter = null) {
    let query = supabase?.from('bundles_catalog')?.select('*')?.order('bundle_name', { ascending: true });
    if (statusFilter) {
      query = query?.eq('catalog_status', statusFilter);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(dbToBundle);
  },

  async getActive() {
    const { data, error } = await supabase
      ?.from('bundles_catalog')
      ?.select('id, bundle_name, bundle_category, target_market, estimated_value, catalog_status')
      ?.eq('catalog_status', 'active')
      ?.order('bundle_name', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(dbToBundle);
  },

  async create(bundleData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('bundles_catalog')?.insert({
      bundle_name: bundleData?.bundleName,
      description: bundleData?.description,
      bundle_category: bundleData?.bundleCategory,
      target_market: bundleData?.targetMarket,
      estimated_value: bundleData?.estimatedValue,
      included_products: bundleData?.includedProducts,
      included_services: bundleData?.includedServices,
      reference_use_cases: bundleData?.referenceUseCases,
      catalog_status: bundleData?.catalogStatus || 'active',
      created_by: user?.id || null,
    })?.select()?.single();
    if (error) throw error;
    return dbToBundle(data);
  },

  async update(id, bundleData) {
    const { data, error } = await supabase?.from('bundles_catalog')?.update({
      bundle_name: bundleData?.bundleName,
      description: bundleData?.description,
      bundle_category: bundleData?.bundleCategory,
      target_market: bundleData?.targetMarket,
      estimated_value: bundleData?.estimatedValue,
      included_products: bundleData?.includedProducts,
      included_services: bundleData?.includedServices,
      reference_use_cases: bundleData?.referenceUseCases,
      catalog_status: bundleData?.catalogStatus,
    })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToBundle(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('bundles_catalog')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

function dbToBundle(row) {
  if (!row) return null;
  return {
    id: row?.id,
    bundleName: row?.bundle_name,
    description: row?.description,
    bundleCategory: row?.bundle_category,
    targetMarket: row?.target_market,
    estimatedValue: row?.estimated_value,
    includedProducts: row?.included_products,
    includedServices: row?.included_services,
    referenceUseCases: row?.reference_use_cases,
    catalogStatus: row?.catalog_status,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
  };
}
